import { Suspense, lazy, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import Cookies from 'js-cookie'
import { Helmet } from 'react-helmet'

import { formatEpsilonPageVisitForAnalytics } from 'utilities/analytics/formatItineraryForAnalytics'
import fireDataLayerPush from 'utilities/analytics/fireDataLayerPush'
import Footer from '../components/Footer/Footer'
import { selectConfigState } from '../redux/configuration'
import bootstrapApp from '../utilities/bootstrapApp'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'

import {
  handleReceiveUser,
  selectUser,
  selectUserRole,
} from '../redux/authentication'

import { redFunnelAPI } from '../api/api'
import {
  fetchUserVehicles,
  selectUserVehicleState,
} from '../redux/userVehicles'

const NewBookingAppContainer = lazy(() => import('./NewBookingAppContainer'))
const MyAccountApp = lazy(() => import('./MyAccountApp'))
const AgentPortal = lazy(() => import('./AgentPortal'))
const CallCentreApp = lazy(() => import('./CallCentreApp'))

function App() {
  const {
    loading: configLoading,
    data: configData,
    error: configError,
  } = useSelector(selectConfigState)
  const dispatch = useDispatch()
  const location = useLocation()
  const userRole = useSelector(selectUserRole)
  const userVehicleState = useSelector(selectUserVehicleState)
  useEffect(() => {
    // legacy red funnel page needs redirecting
    if (location?.pathname?.includes('/book/basket/login/agent')) {
      window.location.href = `${process.env.REACT_APP_DRUPAL_HOME}`
    }
  }, [location.pathname])

  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    if (!configData && !configLoading && !configError) {
      dispatch(bootstrapApp()).finally(async () => {
        const storageId = sessionStorage.getItem('rfSessionIId')
        const params = queryString.parse(window.location.search)
        const sessionIdParam = params?.sessionId
        const awc = params?.awc
        if (storageId) {
          try {
            const user = JSON.parse(sessionStorage.getItem(storageId))
            if (user) {
              const userId =
                user?.customerAccount?.customerNumber ||
                user?.agentUser?.agentNumber ||
                user?.agentNumber ||
                user?.ccId ||
                ''
              if (userId) {
                try {
                  await redFunnelAPI.validateSession(userId, storageId)
                  dispatch(handleReceiveUser(user))
                  redFunnelAPI.setSessionId(storageId)
                } catch (err) {
                  sessionStorage.removeItem(storageId)
                  sessionStorage.removeItem('rfSessionIId')
                }
              } else {
                sessionStorage.removeItem(storageId)
                sessionStorage.removeItem('rfSessionIId')
              }
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') {
              console.log(err) //eslint-disable-line
            }
          }
        } else if (sessionIdParam) {
          redFunnelAPI.setSessionId(sessionIdParam)
        }

        if (awc) {
          Cookies.set('awc', awc, { expires: 0.5 })
        }

        setIsLoading(false)
      })
    }
  }, [dispatch, configData, isLoading, configError, configLoading])

  const user = useSelector(selectUser)

  useEffect(() => {
    if (
      user &&
      userRole === 'customer' &&
      !userVehicleState.error &&
      !userVehicleState.loading &&
      !userVehicleState.vehicles
    ) {
      dispatch(fetchUserVehicles())
    }
  }, [user, userRole])

  useEffect(() => {
    formatEpsilonPageVisitForAnalytics(user, location.pathname).then(
      epsilonData => {
        if (epsilonData && Object.keys(epsilonData).length > 0)
          fireDataLayerPush('epsilon_page_view', epsilonData)
      },
    )
  }, [location.pathname])

  const renderUi = () => {
    if (configError) {
      return (
        <div className="flex-1 pt-10 text-center">
          <p className="font-bold text-red">{configError}</p>
        </div>
      )
    }
    if (isLoading) {
      return (
        <div className="pt-10 flex items-center flex-1">
          <LoadingSpinner />
        </div>
      )
    }
    return (
      <Routes>
        <Route
          path="/book/*"
          element={
            <>
              <Helmet>
                <meta name="description" content="Red Funnel" />
              </Helmet>
              <NewBookingAppContainer user={user} />
            </>
          }
        />
        <Route path="/my-account/*" element={<MyAccountApp user={user} />} />
        <Route path="/agent-portal/*" element={<AgentPortal user={user} />} />
        <Route
          path="/contact-centre/*"
          element={<CallCentreApp user={user} />}
        />
      </Routes>
    )
  }

  return (
    <div className="app flex flex-col  min-h-full">
      <Suspense fallback={<LoadingSpinner />}>
        {renderUi()}
        <Footer baseUrl={process.env.REACT_APP_DRUPAL_HOME} />
      </Suspense>
    </div>
  )
}

export default App
